import { createContext, useState, FC, useContext, createElement } from 'react';
import {
  defaultMultiVoucher,
  type MultiVoucherFormSchema,
} from '../form/schema';
import { SignUpStep3Schema as CreditCardForm } from 'blocks/sign-up/schemas';
import { CurrencyType, GiftCard } from 'types/types';

type ServerSideProps = {
  currencies: CurrencyType[];
  customGiftCards: GiftCard[];
};

export type State = MultiVoucherFormSchema & CreditCardForm & ServerSideProps;

type Dispatch = (newState: Partial<State>) => void;

type VoucherProviderProps = {
  children: React.ReactNode;
} & ServerSideProps;

const VoucherContext = createContext<
  | {
      state: State;
      setState: Dispatch;
      resetState: () => void;
    }
  | undefined
>(undefined);

const initialState: MultiVoucherFormSchema & CreditCardForm = {
  ...defaultMultiVoucher,
  creditCard: {
    brand: '',
    name: '',
    number: '',
    expiry: '',
    cvc: '',
  },
};

const VoucherProvider: FC<VoucherProviderProps> = ({
  children,
  currencies,
  customGiftCards,
}) => {
  const [state, setState] = useState<State>({
    ...initialState,
    currencies,
    customGiftCards,
  });

  const dispatchSliceOfState = (
    newState: Partial<MultiVoucherFormSchema & CreditCardForm>
  ) => {
    setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  };

  const resetState = () => {
    dispatchSliceOfState(initialState);
  };

  return createElement(
    VoucherContext.Provider,
    { value: { state, setState: dispatchSliceOfState, resetState } },
    children
  );
};

const useVoucherContext = () => {
  const context = useContext(VoucherContext);
  if (context === undefined) {
    throw new Error('useVoucherContext must be used within a VoucherProvider');
  }
  return context;
};

export { VoucherProvider, useVoucherContext };
